import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ONBOARDING_SLIDES,
  resolveMedia,
  type OnboardingSlide,
} from '../onboarding/onboardingSlides';
import { ProgressiveImage, SlideVideo } from '../onboarding/slideMedia';
import { useIsDesktop } from '../onboarding/useIsDesktop';
import { dsColors } from '../../design-system/tokens/colors';

/**
 * Hero del paywall — banda superior del rediseño de Figma "Pricing"
 * (node 2044:448, frame "Head"). Ver ADR-2026-07-28-rediseno-paywall-catalogo-componentes-presentacionales.
 *
 * Reutiliza las slides y la maquinaria de fondo del carrusel de bienvenida
 * (`ONBOARDING_SLIDES` + `slideMedia`): mismo auto-avance tipo stories, mismos
 * videos/imágenes progresivos y mismos sets por rubro. La diferencia con
 * `OnboardingCarousel` es el encuadre (banda de alto acotado, no pantalla
 * completa) y que acá NO hay CTA ni pill de promo: ese espacio lo ocupan el
 * botón de cerrar y el switch Mensual/Anual, que van fijos y los inyecta el
 * contenedor (`overlay`).
 *
 * A diferencia del carrusel, el hero NO captura gestos: el usuario está
 * scrolleando el catálogo por encima, así que sólo se navega tocando los
 * segmentos de la barra de progreso.
 */

// Mismo ritmo que el carrusel de bienvenida.
const SLIDE_DURATION = 6000;

// Gradiente de legibilidad: transparente arriba → negro abajo (stops del frame).
const SCRIM = 'linear-gradient(to bottom, rgba(0,0,0,0) 41%, rgba(0,0,0,0.98) 99%)';

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  );
}

export interface PricingHeroProps {
  slides?: OnboardingSlide[];
  /** Chrome fijo (cerrar + switch de facturación) que se dibuja sobre la imagen. */
  overlay?: React.ReactNode;
  /** Clases del alto de la banda. Default: la proporción del frame en mobile. */
  heightClassName?: string;
  /**
   * Título + bajada + barra de progreso. Se puede apagar cuando el contenedor ya
   * pone su propio copy y dos titulares competirían (el panel de venta del modal
   * de desktop, ADR-2026-07-29-desktop-paywall-modal-dos-paneles). Default `true`: nadie más cambia.
   */
  showCopy?: boolean;
  /**
   * Clases del contenedor raíz. En desktop (ADR-2026-07-29-reparto-contenido-modal-desktop-carrusel) el hero llena el panel
   * izquierdo del modal, y para que `heightClassName="h-full"` tenga contra qué
   * medir la raíz también necesita alto.
   */
  className?: string;
  /**
   * Alineación del copy y la barra de progreso. `center` es el frame de mobile;
   * `left` es el del onboarding en desktop (ADR-2026-07-30-iteracion-modal-desktop-solo-eje), donde el panel es alto y
   * angosto y el texto centrado quedaba flotando.
   */
  copyAlign?: 'center' | 'left';
  /**
   * Copy FIJO que reemplaza el título y la bajada de cada slide y se mantiene a lo
   * largo de todo el carrusel (ADR-2026-07-30-copy-catalogo-segun-contexto-abrio). Lo usan los catálogos que se abren desde
   * un contexto concreto —final del alta, sin créditos— para que el argumento no
   * se pierda mientras las imágenes rotan. Los pasos siguen avanzando igual.
   */
  fixedCopy?: { title: string; description: string };
}

export function PricingHero({
  slides = ONBOARDING_SLIDES,
  overlay,
  heightClassName = 'h-[58svh] max-h-[540px] min-h-[280px] lg:h-[300px] lg:min-h-0',
  className = '',
  showCopy = true,
  copyAlign = 'center',
  fixedCopy,
}: PricingHeroProps) {
  const count = slides.length;
  const isDesktop = useIsDesktop();
  const [index, setIndex] = useState(0);
  const [progress, setProgress] = useState(0); // 0..1 del slide activo
  const [reduceMotion] = useState(prefersReducedMotion);

  const pausedRef = useRef(false);
  const elapsedRef = useRef(0);

  const goTo = useCallback(
    (next: number) => {
      setIndex(((next % count) + count) % count);
      elapsedRef.current = 0;
      setProgress(0);
    },
    [count],
  );

  // Auto-avance (rAF), idéntico al del carrusel: el segmento activo rellena
  // 0→100% y al llegar avanza. Se congela con la pestaña oculta y no corre con
  // reduced-motion.
  useEffect(() => {
    elapsedRef.current = 0;
    setProgress(0);
    if (reduceMotion) return;

    let raf = 0;
    let prevT: number | null = null;
    const loop = (t: number) => {
      if (prevT == null) prevT = t;
      const dt = t - prevT;
      prevT = t;
      if (!pausedRef.current) {
        elapsedRef.current += dt;
        const p = Math.min(elapsedRef.current / SLIDE_DURATION, 1);
        setProgress(p);
        if (p >= 1) {
          setIndex((i) => (i + 1) % count);
          return; // el cambio de `index` re-dispara el efecto
        }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [index, count, reduceMotion]);

  useEffect(() => {
    const onVis = () => {
      pausedRef.current = document.hidden;
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // Navegación por gesto, igual que el carrusel de bienvenida: arrastrar a un
  // lado u otro cambia de imagen, y un tap sin arrastre va a la mitad izquierda
  // (atrás) o derecha (adelante). `touchAction: pan-y` deja pasar el scroll
  // vertical del catálogo, que es lo que el usuario hace la mayor parte del tiempo.
  const dragRef = useRef<{ x: number; y: number; moved: boolean } | null>(null);

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return; // sólo botón primario
    dragRef.current = { x: e.clientX, y: e.clientY, moved: false };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // pointer no activo: seguimos sin capture.
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const s = dragRef.current;
    if (!s) return;
    if (Math.abs(e.clientX - s.x) > 8 || Math.abs(e.clientY - s.y) > 8) s.moved = true;
  };

  const onPointerUp = (e: React.PointerEvent) => {
    const s = dragRef.current;
    dragRef.current = null;
    if (!s) return;
    const dx = e.clientX - s.x;
    const dy = e.clientY - s.y;
    if (s.moved) {
      // Sólo si el gesto fue claramente horizontal: si predominó la vertical, el
      // usuario estaba scrolleando el catálogo y no hay que cambiar de imagen.
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
        if (dx < 0) goTo(index + 1);
        else goTo(index - 1);
      }
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      if (e.clientX - rect.left > rect.width / 2) goTo(index + 1);
      else goTo(index - 1);
    }
  };

  const onPointerCancel = () => {
    dragRef.current = null;
  };

  const slide = slides[index];

  return (
    <div className={`relative w-full overflow-hidden bg-black ${className}`}>
      {/* Alto de la banda: proporción del frame (402×540) acotada al viewport en
          mobile, y más baja en desktop, donde el catálogo va en 3 columnas y el
          hero es sólo un encabezado. */}
      <div className={`relative w-full ${heightClassName}`}>
        {slides.map((slideBase, i) => {
          const s = resolveMedia(slideBase, isDesktop);
          // El encuadre por defecto de las slides es `top`, pensado para el
          // carrusel full-bleed vertical. Acá, en desktop, el hero es una banda
          // ancha y baja: anclar arriba recorta las caras. Si la slide no pide un
          // encuadre propio, en desktop se centra.
          const framing = s.imagePosition ?? (isDesktop ? 'center' : undefined);
          const videoFraming = s.videoPosition ?? framing;
          return (
            <div
              key={slideBase.image}
              aria-hidden={i === index ? undefined : true}
              className="absolute inset-0 transition-opacity duration-700 ease-out"
              style={{ opacity: i === index ? 1 : 0 }}
            >
              <ProgressiveImage
                preview={s.preview}
                src={s.image}
                alt={i === index ? s.alt : ''}
                objectPosition={framing}
              />
              {s.video && !reduceMotion ? (
                <SlideVideo
                  key={s.video}
                  preview={s.videoPreview ?? s.video}
                  src={s.video}
                  poster={s.image}
                  active={i === index}
                  objectPosition={videoFraming}
                />
              ) : null}
            </div>
          );
        })}

        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{ background: SCRIM }}
        />

        {/* Capa de gestos: cubre la banda entera, por encima de las imágenes y por
            debajo del contenido. Va aparte (y no en el contenedor) para que los
            segmentos de la barra de progreso no disparen además la navegación por
            tap: si burbujeara, un click en un segmento avanzaría dos veces. */}
        <div
          className="absolute inset-0 z-[1]"
          style={{ touchAction: 'pan-y', cursor: 'pointer' }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
        />

        {/* Copy de la slide + progreso, anclados al pie de la banda.
            `pointer-events-none` para que los taps atraviesen hasta la capa de
            gestos; sólo la barra de progreso vuelve a escuchar. */}
        {showCopy ? (
        <div
          className={`pointer-events-none absolute inset-x-0 bottom-0 z-10 flex flex-col gap-1 px-9 pb-10 ${
            copyAlign === 'left' ? 'items-start text-left' : 'items-center text-center'
          }`}
        >
          {/* Con `fixedCopy` NO va la `key={index}`: sin remontar por slide, el
              texto se queda quieto mientras las imágenes rotan (que es el punto)
              en vez de re-animar su entrada cuatro veces. */}
          <div
            key={fixedCopy ? 'fixed' : index}
            className={`flex flex-col gap-1.5 ${fixedCopy ? '' : 'animate-fade-in'}`}
          >
            <h2
              className="text-[20px] font-[600] leading-[25px]"
              style={{ color: dsColors.neutral[50] }}
            >
              {fixedCopy?.title ?? slide.title}
            </h2>
            <p
              className="text-[14px] font-[600] leading-[19px]"
              style={{ color: dsColors.neutral[300] }}
            >
              {fixedCopy?.description ?? slide.description}
            </p>
          </div>
          <div className="pointer-events-auto">
            <HeroProgress
              count={count}
              index={index}
              progress={reduceMotion ? 1 : progress}
              onSelect={goTo}
            />
          </div>
        </div>
        ) : null}

        {overlay}
      </div>
    </div>
  );
}

/**
 * Barra de progreso del hero. A diferencia de la del carrusel (segmentos de
 * ancho igual), acá el segmento **activo** es más ancho y se rellena en azul de
 * marca, como en el frame de Figma.
 */
function HeroProgress({
  count,
  index,
  progress,
  onSelect,
}: {
  count: number;
  index: number;
  progress: number;
  onSelect: (i: number) => void;
}) {
  return (
    <div className="mt-3 flex items-center gap-3">
      {Array.from({ length: count }).map((_, i) => {
        const active = i === index;
        const fill = i < index ? 100 : active ? progress * 100 : 0;
        return (
          <button
            key={i}
            type="button"
            onClick={() => onSelect(i)}
            aria-label={`Ir a la pantalla ${i + 1} de ${count}`}
            aria-current={active ? 'true' : undefined}
            className="flex h-5 items-center transition-[width] duration-500 ease-out"
            style={{ width: active ? 41 : 18 }}
          >
            <span
              className="h-[6px] w-full overflow-hidden rounded-full"
              style={{ background: dsColors.neutral[400] }}
            >
              <span
                className="block h-full rounded-full"
                style={{ width: `${fill}%`, background: dsColors.brand[500] }}
              />
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default PricingHero;
